import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RedditPost } from '../types/reddit';
import { getSubredditData, getSubreddits } from '../services/redditService';
import ArticlePreview from './ArticlePreview';
import RedditHeatmap from './RedditHeatmap';
import Header from './Header';
import Footer from './Footer';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [subreddits, setSubreddits] = useState<string[]>([]);
  const [selectedSubreddit, setSelectedSubreddit] = useState<string>('technology');
  const [posts, setPosts] = useState<RedditPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchSubreddits = async () => {
      try {
        const subs = await getSubreddits();
        setSubreddits(subs);
      } catch (error) {
        console.error("Failed to fetch subreddits", error);
      }
    };
    fetchSubreddits();
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const data = await getSubredditData(selectedSubreddit);
        setPosts(data.posts);
      } catch (error) {
        console.error(`Failed to fetch posts for ${selectedSubreddit}`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [selectedSubreddit]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Header />
      <main className="flex-grow container mx-auto px-6 py-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-8 text-center">{t('dashboard.title')}</h1>
        
        <div className="mb-8">
          <label htmlFor="subreddit-select" className="block text-lg font-medium text-gray-700 mb-2">
            {t('dashboard.select_subreddit')}
          </label>
          <select
            id="subreddit-select"
            value={selectedSubreddit}
            onChange={(e) => setSelectedSubreddit(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            {subreddits.map((sub) => (
              <option key={sub} value={sub}>{sub}</option>
            ))}
          </select>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            {loading ? (
              <p>Loading posts...</p>
            ) : (
              <div className="space-y-6">
                {posts.map((post) => (
                  <ArticlePreview key={post.id} post={post} />
                ))}
              </div>
            )}
          </div>
          <div className="md:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold mb-4">{t('dashboard.heatmap_title')}</h3>
              <RedditHeatmap />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;